import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type TabStatus = 'idle' | 'running' | 'completed' | 'failed';

export interface ChatTab {
  id: string; // Can be 'welcome' or 'session-{sessionId}'
  title: string;
  sessionId: string | null; // null for welcome tab
  isPinned: boolean;
  lastActivity: number;
  status?: TabStatus; // For indicating running scheduled tasks
  scheduleId?: string; // Associated schedule ID if this tab is from a schedule execution
}

interface ChatTabsStore {
  tabs: ChatTab[];
  activeTabId: string;
  _version: number; // Internal counter to force re-renders

  // Tab management
  addTab: (tab: Omit<ChatTab, 'lastActivity'>) => void;
  removeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  updateTabTitle: (tabId: string, title: string) => void;
  updateTabStatus: (tabId: string, status: TabStatus) => void;
  getTabBySessionId: (sessionId: string) => ChatTab | undefined;
  closeTab: (tabId: string) => void;
  closeOtherTabs: (tabId: string) => void;
  closeAllTabs: () => void;
  getTab: (tabId: string) => ChatTab | undefined;
}

const initialWelcomeId = `welcome-${Date.now()}`;

export const useChatTabsStore = create<ChatTabsStore>()(
  persist(
    (set, get) => ({
      tabs: [
        {
          id: initialWelcomeId,
          title: 'New Chat',
          sessionId: null,
          isPinned: false,
          lastActivity: Date.now(),
        },
      ],
      activeTabId: initialWelcomeId,
      _version: 0,

      addTab: (tab) => {
        const { tabs, activeTabId, _version } = get();
        const existingTab = tabs.find((t) => t.id === tab.id);

        if (existingTab) {
          // Tab already exists, just activate it and update last activity
          set({
            activeTabId: tab.id,
            tabs: tabs.map((t) =>
              t.id === tab.id ? { ...t, lastActivity: Date.now() } : t
            ),
            _version: _version + 1,
          });
        } else {
          // Check if the active tab is an unused welcome tab that should be replaced
          const activeTab = tabs.find((t) => t.id === activeTabId);
          const isActiveWelcomeTab = activeTab && activeTab.sessionId === null && !activeTab.isPinned;

          if (isActiveWelcomeTab && tab.sessionId !== null) {
            // Replace the welcome tab with the new session tab
            set({
              tabs: tabs.map((t) =>
                t.id === activeTabId ? { ...tab, lastActivity: Date.now() } : t
              ),
              activeTabId: tab.id,
              _version: _version + 1,
            });
          } else {
            // Add new tab
            set({
              tabs: [...tabs, { ...tab, lastActivity: Date.now() }],
              activeTabId: tab.id,
              _version: _version + 1,
            });
          }
        }
      },

      removeTab: (tabId) => {
        const { tabs, activeTabId, _version } = get();
        const newTabs = tabs.filter((t) => t.id !== tabId);

        // If this is the last tab, create a new welcome tab
        if (newTabs.length === 0) {
          const uniqueId = `welcome-${Date.now()}`;
          set({
            tabs: [
              {
                id: uniqueId,
                title: 'New Chat',
                sessionId: null,
                isPinned: false,
                lastActivity: Date.now(),
              },
            ],
            activeTabId: uniqueId,
            _version: _version + 1,
          });
          return;
        }

        let newActiveTabId = activeTabId;

        // If we're removing the active tab, switch to another
        if (activeTabId === tabId) {
          const closedIndex = tabs.findIndex((t) => t.id === tabId);
          const newIndex = Math.min(closedIndex, newTabs.length - 1);
          newActiveTabId = newTabs[newIndex]?.id;
        }

        set({
          tabs: newTabs,
          activeTabId: newActiveTabId,
          _version: _version + 1,
        });
      },

      setActiveTab: (tabId) => {
        const { tabs, _version } = get();
        if (tabs.find((t) => t.id === tabId)) {
          set({
            activeTabId: tabId,
            tabs: tabs.map((t) =>
              t.id === tabId ? { ...t, lastActivity: Date.now() } : t
            ),
            _version: _version + 1,
          });
        }
      },

      updateTabTitle: (tabId, title) => {
        console.log('[ChatTabsStore] updateTabTitle called:', { tabId, title });
        set((state) => {
          const newTabs = state.tabs.map((t) => (t.id === tabId ? { ...t, title } : t));
          console.log('[ChatTabsStore] Tabs before:', state.tabs);
          console.log('[ChatTabsStore] Tabs after:', newTabs);
          return {
            tabs: newTabs,
            _version: state._version + 1 // Increment to force re-render
          };
        });
      },

      updateTabStatus: (tabId, status) => {
        set((state) => ({
          tabs: state.tabs.map((t) => (t.id === tabId ? { ...t, status } : t)),
          _version: state._version + 1,
        }));
      },

      getTabBySessionId: (sessionId) => {
        return get().tabs.find((t) => t.sessionId === sessionId);
      },

      closeTab: (tabId) => {
        get().removeTab(tabId);
      },

      closeOtherTabs: (tabId) => {
        const { tabs, _version } = get();
        const tabToKeep = tabs.find((t) => t.id === tabId);
        if (tabToKeep) {
          set({
            tabs: [tabToKeep],
            activeTabId: tabId,
            _version: _version + 1,
          });
        }
      },

      closeAllTabs: () => {
        // Reset to just a new welcome tab
        const uniqueId = `welcome-${Date.now()}`;
        set((state) => ({
          tabs: [
            {
              id: uniqueId,
              title: 'New Chat',
              sessionId: null,
              isPinned: false,
              lastActivity: Date.now(),
            },
          ],
          activeTabId: uniqueId,
          _version: state._version + 1, // Increment to force re-render
        }));
      },

      getTab: (tabId) => {
        return get().tabs.find((t) => t.id === tabId);
      },
    }),
    {
      name: 'chat-tabs-storage',
      partialize: (state) => ({
        tabs: state.tabs,
        activeTabId: state.activeTabId,
        _version: state._version,
      }),
    }
  )
);
